import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { useBooking } from "@/app/Bookings/Hooks/useBooking";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { blockBookingCustomerMutation } from "@/app/Bookings/GraphQL/blocklistMutations";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";

type BookingBlockCustomerDialogProps = {
  bookingId: string;
};

const validationSchema = z.object({
  reason: z
    .string()
    .min(1, "Reason is required")
    .max(255, "Reason must be 255 characters or less"),
});

export function BookingBlockCustomerDialog({
  bookingId,
}: BookingBlockCustomerDialogProps) {
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(blockBookingCustomerMutation);
  const booking = useBooking(bookingId);

  return (
    <BaseDialog title="Block Customer">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Are you sure you want to block <strong>{booking.name}</strong>?
        </p>
        <p className="text-sm text-gray-600">
          This will prevent them from making future bookings using:
        </p>
        <ul className="list-disc pl-5 text-sm text-gray-600">
          <li>Email: {booking.email}</li>
          <li>Phone: {booking.phone}</li>
          <li>User account</li>
        </ul>

        <GenericForm
          initialValues={{
            reason: "",
          }}
          validationSchema={toFormikValidationSchema(validationSchema)}
          onSubmit={async (values) => {
            await mutateAsync({
              input: {
                booking_id: bookingId,
                reason: values.reason,
              },
            });

            await close();
          }}
        >
          <TextAreaInput
            autoFocus
            name="reason"
            label="Reason"
            className="col-span-full"
          />

          <DialogSubmitButton text="Block Customer" />
        </GenericForm>
      </div>
    </BaseDialog>
  );
}
